import * as React from 'react'

interface Props {
  minBoundCents: number
  maxBoundCents: number
  // current selection in cents; null = open-ended on that end (the track extent)
  minCents: number | null
  maxCents: number | null
  onChange: (minCents: number | null, maxCents: number | null) => void
}

type Handle = 'min' | 'max'

const poundsLabel = (pounds: number): string =>
  `£${pounds.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`

// Custom dual-handle price slider — built on Pointer Events (so it works with
// mouse, touch AND pen; native <input type=range> stacking was unreliable on
// mobile and wouldn't align). Works in whole pounds; a handle at its extreme
// emits null for that end (no bound). Dragging updates only the local position;
// the value is committed to the parent (→ refetch) on RELEASE, not per move.
const PriceRangeSlider: React.FC<Props> = ({
  minBoundCents,
  maxBoundCents,
  minCents,
  maxCents,
  onChange
}) => {
  const lo = Math.floor(minBoundCents / 100)
  const hi = Math.ceil(maxBoundCents / 100)
  const trackRef = React.useRef<HTMLDivElement>(null)

  const clamp = (v: number): number => Math.min(hi, Math.max(lo, v))
  const propLo = minCents != null ? clamp(Math.round(minCents / 100)) : lo
  const propHi = maxCents != null ? clamp(Math.round(maxCents / 100)) : hi

  const [drag, setDrag] = React.useState<{
    which: Handle
    lo: number
    hi: number
  } | null>(null)

  const dispLo = drag ? drag.lo : propLo
  const dispHi = drag ? drag.hi : propHi

  const commit = (nextLo: number, nextHi: number): void => {
    onChange(
      nextLo <= lo ? null : nextLo * 100,
      nextHi >= hi ? null : nextHi * 100
    )
  }

  const step = Math.max(1, Math.round((hi - lo) / 50))

  const valueFromClientX = (clientX: number): number => {
    const el = trackRef.current

    if (!el) return lo

    const rect = el.getBoundingClientRect()
    const ratio = rect.width ? (clientX - rect.left) / rect.width : 0

    return clamp(Math.round(lo + ratio * (hi - lo)))
  }

  const nearestHandle = (value: number): Handle => {
    if (value <= dispLo) return 'min'
    if (value >= dispHi) return 'max'
    return value - dispLo <= dispHi - value ? 'min' : 'max'
  }

  // move one handle to `value`, keeping min <= max against the other handle
  const withHandle = (
    which: Handle,
    value: number,
    baseLo: number,
    baseHi: number
  ): { lo: number; hi: number } =>
    which === 'min'
      ? { lo: Math.min(value, baseHi), hi: baseHi }
      : { lo: baseLo, hi: Math.max(value, baseLo) }

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    const value = valueFromClientX(e.clientX)
    const which = nearestHandle(value)

    setDrag({ which, ...withHandle(which, value, propLo, propHi) })
    trackRef.current?.setPointerCapture(e.pointerId)
  }

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!drag) return

    const value = valueFromClientX(e.clientX)

    setDrag((d) =>
      d ? { which: d.which, ...withHandle(d.which, value, d.lo, d.hi) } : d
    )
  }

  const endDrag = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!drag) return

    commit(drag.lo, drag.hi)
    setDrag(null)
    trackRef.current?.releasePointerCapture?.(e.pointerId)
  }

  // keyboard + wheel nudge one handle by a step and commit immediately (discrete,
  // not a continuous drag)
  const nudge = (which: Handle, delta: number): void => {
    const value = clamp((which === 'min' ? propLo : propHi) + delta)
    const next = withHandle(which, value, propLo, propHi)

    commit(next.lo, next.hi)
  }

  const onHandleKeyDown =
    (which: Handle) =>
    (e: React.KeyboardEvent<HTMLDivElement>): void => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
        e.preventDefault()
        nudge(which, -step)
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
        e.preventDefault()
        nudge(which, step)
      }
    }

  // Wheel-to-adjust: nudge the handle nearest the cursor and stop the page
  // scrolling. React's onWheel is passive, so bind non-passively.
  React.useEffect(() => {
    const el = trackRef.current

    if (!el || hi <= lo) return undefined

    const handleWheel = (e: WheelEvent): void => {
      e.preventDefault()
      nudge(
        nearestHandle(valueFromClientX(e.clientX)),
        e.deltaY < 0 ? step : -step
      )
    }

    el.addEventListener('wheel', handleWheel, { passive: false })
    return () => el.removeEventListener('wheel', handleWheel)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propLo, propHi, dispLo, dispHi, lo, hi, step])

  if (hi <= lo) {
    return <div className="price-slider__single">{poundsLabel(lo)}</div>
  }

  const pct = (v: number): number => ((v - lo) / (hi - lo)) * 100

  return (
    <div className="price-slider">
      <div className="price-slider__readout">
        <span>{poundsLabel(dispLo)}</span>
        <span>{poundsLabel(dispHi)}</span>
      </div>
      <div
        className="price-slider__track"
        ref={trackRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <div className="price-slider__rail" />
        <div
          className="price-slider__fill"
          style={{
            left: `${pct(dispLo)}%`,
            width: `${pct(dispHi) - pct(dispLo)}%`
          }}
        />
        <div
          className="price-slider__handle"
          role="slider"
          tabIndex={0}
          aria-label="Minimum price"
          aria-valuemin={lo}
          aria-valuemax={hi}
          aria-valuenow={dispLo}
          style={{ left: `${pct(dispLo)}%` }}
          onKeyDown={onHandleKeyDown('min')}
        />
        <div
          className="price-slider__handle"
          role="slider"
          tabIndex={0}
          aria-label="Maximum price"
          aria-valuemin={lo}
          aria-valuemax={hi}
          aria-valuenow={dispHi}
          style={{ left: `${pct(dispHi)}%` }}
          onKeyDown={onHandleKeyDown('max')}
        />
      </div>
    </div>
  )
}

export default PriceRangeSlider
